import { BarChartOutlined, CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { IAnalyticsProcessorConfig } from "@kuika/kuika-cl-model";
import { memo, useEffect, useMemo, useState } from "react";
import { Handle, Position, useReactFlow, useStore } from "reactflow";
import KuikaSvg from "../../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { useHistory } from "../history-context";
import { AnalyticsProcessorZonesModal } from "./AnalyticsProcessorZonesModal";
import ExtendingConnector from "./ExtendingConnector";
import { NODE_TOOLTIPS } from "./nodesInfoContent";
import { useNodeCreation } from "./useNodeCreation";
import baseStyles from "./BaseNode.module.css";

interface AnalyticsProcessorNodeData {
  type: "AnalyticsProcessor";
  title: string;
  config?: IAnalyticsProcessorConfig;
}

interface AnalyticsProcessorNodeProps {
  data: AnalyticsProcessorNodeData;
  id: string;
  selected?: boolean;
}

const NODE_CATEGORY_STYLES = {
  analytics: {
    primaryColor: "#f59e0b",
    headerBackground: "rgba(245, 158, 11, 0.1)",
    iconColor: "#f59e0b",
    handleColor: "#f59e0b"
  }
};

const DEFAULT_EVENT_TYPES = [
  // "bucket_drop",
  // "cycle_complete",
  // "anomaly",
  // "customer_arrival",
  // "order_placed",
  // "seat_taken",
  // "seat_left",
  // "dwell_time",
  // "zone_occupancy"
  "object_entered",
  "object_exited"
];

const AnalyticsProcessorNode = ({ data, id, selected }: AnalyticsProcessorNodeProps) => {
  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Normalize confidence to integer 0-100: if value is a decimal (0-1), convert to percentage
  const normalizeConfidence = (value: number | undefined): number => {
    if (value === undefined) return 80;
    // If value is between 0 and 1 (exclusive), treat as decimal and convert to percentage
    if (value > 0 && value < 1) return Math.round(value * 100);
    // Otherwise treat as already a percentage (0-100)
    return Math.round(value);
  };

  const initialThreshold = normalizeConfidence((data.config as any)?.confidenceThreshold);

  const [config, setConfig] = useState(() => {
    // Default config values matching backend VisionDataConfigModel
    const defaults = {
      zones: [] as IAnalyticsProcessorConfig["zones"],
      eventTypes: [] as string[],
      confidenceThreshold: 80,
      cooldownMs: 5000,
      previewSampleUrl: ""
    };
    // Merge with provided config from template
    if (data.config) {
      return {
        ...defaults,
        ...data.config,
        // Ensure confidence threshold is normalized
        confidenceThreshold: initialThreshold
      };
    }
    return defaults;
  });
  const { setNodes, getNodes, getEdges } = useReactFlow();
  const { createConnectedNode } = useNodeCreation(id);
  const takeSnapshot = useHistory();

  // Check if source handle is connected - using useStore for reactivity
  const isSourceConnected = useStore(useMemo(() => (state) => state.edges.some((edge) => edge.source === id), [id]));

  const categoryStyles = NODE_CATEGORY_STYLES.analytics;

  const updateConfig = (updates: Partial<typeof config>) => {
    // Take snapshot before each config change for undo support
    if (takeSnapshot) {
      takeSnapshot(getNodes(), getEdges());
    }

    const newConfig = { ...config, ...updates };
    setConfig(newConfig);

    setNodes((nodes) =>
      nodes.map((node) => (node.id === id ? { ...node, data: { ...node.data, config: newConfig } } : node))
    );
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Take snapshot before deleting
    if (takeSnapshot) {
      takeSnapshot(getNodes(), getEdges());
    }
    setNodes((nodes) => nodes.filter((node) => node.id !== id));
  };

  // Sync config state when node data changes from outside (e.g., undo/redo)
  useEffect(() => {
    if (data.config) {
      setConfig((prevConfig) => {
        const stringifiedPrev = JSON.stringify(prevConfig);
        const stringifiedNew = JSON.stringify(data.config);
        // Only update if actually different to avoid unnecessary re-renders
        return stringifiedPrev !== stringifiedNew ? { ...prevConfig, ...data.config } : prevConfig;
      });
    }
  }, [data.config]);

  const toggleEventType = (eventType: string) => {
    const currentEvents = config.eventTypes || [];
    const newEvents = currentEvents.includes(eventType)
      ? currentEvents.filter((e) => e !== eventType)
      : [...currentEvents, eventType];
    updateConfig({ eventTypes: newEvents });
  };

  const handleNodeSelect = (selectedNodeType: string) => {
    createConnectedNode(selectedNodeType);
  };

  const shouldShowConnector = !isSourceConnected || selected;
  const isCompactMode = isSourceConnected && selected;

  const renderInfoIcon = () => (
    <div className={baseStyles.nodeInfoTopRight}>
      <KuikaTooltip
        position="top"
        content={<div className={baseStyles.nodeTooltipContent}>{NODE_TOOLTIPS.analyticsProcessor.text}</div>}
      >
        <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
          <KuikaSvg name="info-circle" width={16} height={16} />
        </span>
      </KuikaTooltip>
    </div>
  );

  const renderSummary = () => {
    return (
      <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
        {renderInfoIcon()}
        <span className={baseStyles.collapsedSummary}>
          {config.zones?.length || 0} zones • {config.eventTypes?.length || 0} events
        </span>
      </div>
    );
  };

  return (
    <div className={`${baseStyles.nodeContainer} ${selected ? "selected" : ""}`}>
      {selected && (
        <button
          className={`${baseStyles.deleteButton} ${baseStyles.nodrag}`}
          onClick={handleDelete}
          onMouseDown={(e) => e.stopPropagation()}
          title="Delete node"
        >
          <KuikaSvg type="common" name="x" width={14} height={14} color="var(--red-5)" />
        </button>
      )}
      <div
        className={`${baseStyles.nodeHeader} ${expanded ? baseStyles.nodeHeaderExpanded : ""}`}
        style={{ background: categoryStyles.headerBackground }}
      >
        <span className={baseStyles.nodeIcon} style={{ color: categoryStyles.iconColor }}>
          <BarChartOutlined />
        </span>
        <span className={baseStyles.nodeTitle}>
          {data.title}
          <KuikaTooltip
            position="top"
            content={<div className={baseStyles.nodeTooltipContent}>{NODE_TOOLTIPS.analyticsProcessor.text}</div>}
          >
            <span className={`${baseStyles.nodeInfoIcon} ${baseStyles.nodrag}`}>
              <KuikaSvg name="info-circle" width={16} height={16} />
            </span>
          </KuikaTooltip>
        </span>
        <button
          onClick={() => setExpanded(!expanded)}
          onMouseDown={(e) => e.stopPropagation()}
          className={`${baseStyles.expandButton} ${baseStyles.nodrag}`}
        >
          {expanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
        </button>
      </div>

      {!expanded && renderSummary()}

      {expanded && (
        <div className={`${baseStyles.nodeContent} ${baseStyles.nodeContentWithInfo}`}>
          {renderInfoIcon()}
          <div className={baseStyles.nodeForm}>
            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Confidence (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={config.confidenceThreshold ?? 80}
                onChange={(e) =>
                  updateConfig({
                    confidenceThreshold: parseInt(e.target.value) || 0
                  })
                }
                className={`${baseStyles.formNumber} ${baseStyles.nodrag}`}
              />
            </div>

            {/* <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Cooldown (ms)</label>
              <input
                type="number"
                className={`${baseStyles.formNumber} ${baseStyles.nodrag}`}
                value={config.cooldownMs}
                onChange={(e) => updateConfig({ cooldownMs: parseInt(e.target.value) })}
                min="0"
                step="100"
              />
            </div> */}

            <div className={baseStyles.formGroup}>
              <label className={baseStyles.formLabel}>Event Types</label>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  maxHeight: "8rem",
                  overflowY: "auto"
                }}
              >
                {DEFAULT_EVENT_TYPES.map((eventType) => (
                  <label
                    key={eventType}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "12px"
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={config.eventTypes?.includes(eventType) || false}
                      onChange={() => toggleEventType(eventType)}
                      className={baseStyles.nodrag}
                    />
                    {eventType.replace(/_/g, " ")}
                  </label>
                ))}
              </div>
            </div>

            <button
              className={`${baseStyles.formButton} ${baseStyles.nodrag}`}
              onClick={(e) => {
                e.stopPropagation();
                setModalOpen(true);
              }}
            >
              Configure Zones
            </button>

            {/* Modal: full configuration editor with preview and zone controls */}
            <AnalyticsProcessorZonesModal
              isOpen={modalOpen}
              config={config}
              onConfigUpdate={(updatedConfig) => updateConfig(updatedConfig)}
              onClose={() => setModalOpen(false)}
            />

            {/* <button
              className={`${baseStyles.formButton} ${baseStyles.nodrag}`}
              onClick={() => setStatus(status === "active" ? "idle" : "active")}
            >
              {status === "active" ? "Stop" : "Start"} Analytics
            </button> */}
          </div>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        className={baseStyles.targetHandle}
        style={{ background: categoryStyles.handleColor }}
      />
      <Handle
        type="source"
        position={Position.Right}
        className={baseStyles.sourceHandle}
        style={{ background: categoryStyles.handleColor }}
      />

      {/* Show extending connector if source handle is not connected OR node is selected */}
      {shouldShowConnector && (
        <ExtendingConnector
          nodeId={id}
          nodeType="analyticsProcessor"
          onNodeSelect={handleNodeSelect}
          compact={isCompactMode}
        />
      )}
    </div>
  );
};

export default memo(AnalyticsProcessorNode);
